import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  CheckCircle,
  Loader2,
  XCircle,
  Key,
  Copy,
  MapPin,
  CalendarDays,
  Clock,
  Mail,
  DoorOpen,
  ArrowRight,
} from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Step 4 of the booking wizard — post-payment confirmation.
 * Handles both Stripe (?session_id=...) and PayPal (?provider=paypal&token=...)
 * return URLs, polls the backend until the booking is confirmed and
 * displays a friendly recap with the access code.
 *
 * Props:
 *  - sessionId: Stripe checkout session id (or null for PayPal)
 *  - paypalOrderId: PayPal order id (or null for Stripe)
 *  - token: bearer token from AuthContext
 *  - onGoDashboard / onBookAnother: navigation callbacks
 */
export default function Step4Confirmation({
  sessionId,
  paypalOrderId,
  token,
  onGoDashboard,
  onBookAnother,
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState('loading');
  const [booking, setBooking] = useState(null);
  const [amount, setAmount] = useState(null);

  const capturePaypal = useCallback(async (orderId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const cap = await axios.post(
        `${API}/payments/paypal/capture`,
        { order_id: orderId },
        { headers, withCredentials: true }
      );
      if (cap.data.status === 'completed' || cap.data.status === 'already_paid') {
        setAmount(cap.data.amount || null);
        try {
          const r = await axios.get(`${API}/bookings`, { headers, withCredentials: true });
          const last = (r.data || [])[0];
          setBooking(last || null);
        } catch { /* ignore */ }
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error('[Step4] PayPal capture error', err);
      setStatus('error');
    }
  }, [token]);

  const pollStripe = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2500;
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/payments/status/${sid}`, {
        headers, withCredentials: true,
      });
      const data = res.data;
      if (data.payment_status === 'paid') {
        setBooking(data.booking || null);
        setAmount(
          typeof data.amount_total === 'number' ? data.amount_total / 100 : null
        );
        setStatus('success');
        return;
      }
      if (data.status === 'expired' || data.payment_status === 'refunded_expired') {
        setStatus('expired');
        return;
      }
      setTimeout(() => pollStripe(sid, attempts + 1), pollInterval);
    } catch (err) {
      console.error('[Step4] Stripe status error', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollStripe(sid, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  }, [token]);

  useEffect(() => {
    if (paypalOrderId) {
      capturePaypal(paypalOrderId);
      return;
    }
    if (sessionId) {
      pollStripe(sessionId, 0);
      return;
    }
    setStatus('error');
  }, [sessionId, paypalOrderId, pollStripe, capturePaypal]);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(t('payment.codeCopied', { defaultValue: 'Code copié !' }));
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up" data-testid="wizard-step-4">
      {/* LOADING STATE */}
      {status === 'loading' && (
        <div className="text-center py-16" data-testid="step4-loading">
          <div className="w-16 h-16 rounded-full bg-[#F0EFEA] flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-8 h-8 text-[#1A3C34] animate-spin" />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-[#1A3C34]">
            {t('payment.verifying', { defaultValue: 'Vérification du paiement...' })}
          </h1>
          <p className="mt-2 text-sm text-[#737373]">
            {t('payment.verifyingHint', { defaultValue: 'Veuillez patienter pendant que nous confirmons votre paiement.' })}
          </p>
        </div>
      )}

      {/* SUCCESS STATE */}
      {status === 'success' && (
        <div data-testid="step4-success">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-9 h-9 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A3C34]">
              {t('payment.paymentConfirmed', { defaultValue: 'Paiement confirmé !' })}
            </h1>
            <p className="mt-2 text-sm text-[#737373]">
              {t('payment.bookingConfirmed', { defaultValue: 'Votre réservation a été confirmée avec succès.' })}
            </p>
          </div>

          {/* Access code — hero card */}
          {booking?.access_code && (
            <div
              className="bg-gradient-to-br from-[#1A3C34] to-[#0f2924] rounded-2xl p-6 sm:p-8 text-center shadow-xl mb-6"
              data-testid="step4-access-code-card"
            >
              <div className="inline-flex items-center gap-1.5 text-[#C77D63] text-xs font-bold uppercase tracking-widest mb-3">
                <Key className="w-3.5 h-3.5" />
                {t('payment.yourAccessCode', { defaultValue: "Votre code d'accès" })}
              </div>
              <div
                className="font-mono text-5xl sm:text-6xl font-black tracking-widest text-white mb-4 select-all"
                data-testid="step4-access-code"
              >
                {booking.access_code}
              </div>
              <button
                type="button"
                onClick={() => copyCode(booking.access_code)}
                data-testid="step4-copy-code-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white/10 hover:bg-white/20 text-white/90 hover:text-white rounded-full text-xs font-semibold transition-colors"
              >
                <Copy className="w-3.5 h-3.5" />
                {t('payment.copyCode', { defaultValue: 'Copier le code' })}
              </button>
            </div>
          )}

          {/* Booking recap */}
          {booking && (
            <div
              className="bg-white border border-[#E2E2DF] rounded-xl p-5 sm:p-6 mb-6 shadow-sm"
              data-testid="step4-recap"
            >
              <h2 className="font-heading text-lg font-bold text-[#1A3C34] mb-4">
                {t('step4.recapTitle', { defaultValue: 'Récapitulatif de votre réservation' })}
              </h2>
              <dl className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#C77D63] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <dt className="text-[11px] uppercase tracking-wider text-[#B7B7B0] font-semibold">
                      {t('booking.space', { defaultValue: 'Espace' })}
                    </dt>
                    <dd className="font-semibold text-[#1A3C34]">
                      {booking.space_name || booking.space_id}
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CalendarDays className="w-4 h-4 text-[#C77D63] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <dt className="text-[11px] uppercase tracking-wider text-[#B7B7B0] font-semibold">
                      {t('booking.date', { defaultValue: 'Date' })}
                    </dt>
                    <dd className="font-semibold text-[#1A3C34]">
                      {booking.date}
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-[#C77D63] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <dt className="text-[11px] uppercase tracking-wider text-[#B7B7B0] font-semibold">
                      {t('booking.hours', { defaultValue: 'Horaires' })}
                    </dt>
                    <dd className="font-mono font-semibold text-[#1A3C34]">
                      {booking.start_time} — {booking.end_time}
                    </dd>
                  </div>
                </div>
                {amount != null && (
                  <div className="pt-3 border-t border-[#E2E2DF] flex items-center justify-between">
                    <span className="text-xs text-[#737373] uppercase tracking-wider font-semibold">
                      {t('booking.totalTvac', { defaultValue: 'Total TVAC' })}
                    </span>
                    <span className="font-heading text-xl font-bold text-[#C77D63]">
                      {Number(amount).toFixed(2)} EUR
                    </span>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Access instructions */}
          <div
            className="bg-[#F0EFEA] rounded-xl p-5 mb-6 border border-[#E2E2DF]"
            data-testid="step4-instructions"
          >
            <div className="flex items-start gap-3">
              <DoorOpen className="w-5 h-5 text-[#1A3C34] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#1A3C34]">
                <p className="font-semibold mb-1">
                  {t('step4.instructionsTitle', { defaultValue: "Comment accéder à l'espace" })}
                </p>
                <p className="text-[#737373] leading-relaxed">
                  {t('step4.instructionsBody', {
                    defaultValue:
                      "Tapez le code d'accès sur le clavier de la porte au début de votre créneau. Le code n'est actif que pendant la période réservée.",
                  })}
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-start gap-3">
              <Mail className="w-5 h-5 text-[#1A3C34] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#737373]">
                {t('step4.emailSent', {
                  defaultValue:
                    'Un e-mail de confirmation avec votre code et votre facture vient de vous être envoyé.',
                })}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="step4-dashboard-btn"
              onClick={onGoDashboard}
              className="flex-1 h-12 bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 rounded-lg font-semibold"
            >
              {t('payment.viewDashboard', { defaultValue: 'Voir mon tableau de bord' })}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              data-testid="step4-book-another-btn"
              variant="outline"
              onClick={onBookAnother}
              className="flex-1 h-12 border-[#1A3C34] text-[#1A3C34] hover:bg-[#F0EFEA] rounded-lg font-semibold"
            >
              {t('payment.bookAnother', { defaultValue: 'Réserver un autre espace' })}
            </Button>
          </div>
        </div>
      )}

      {/* ERROR / TIMEOUT / EXPIRED */}
      {(status === 'error' || status === 'timeout' || status === 'expired') && (
        <div className="text-center py-16" data-testid="step4-error">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${
              status === 'timeout' ? 'bg-amber-100' : 'bg-red-100'
            }`}
          >
            {status === 'timeout' ? (
              <Loader2 className="w-8 h-8 text-amber-600" />
            ) : (
              <XCircle className="w-8 h-8 text-red-600" />
            )}
          </div>
          <h1 className="font-heading text-2xl font-semibold text-[#1A3C34]">
            {status === 'timeout'
              ? t('payment.verifyingTitle', { defaultValue: 'Vérification en cours...' })
              : t('payment.errorTitle', { defaultValue: 'Erreur de paiement' })}
          </h1>
          <p className="mt-2 text-sm text-[#737373] max-w-md mx-auto">
            {status === 'timeout'
              ? t('payment.timeoutHint', {
                  defaultValue:
                    'Votre paiement est en cours de traitement. Consultez votre tableau de bord dans quelques minutes.',
                })
              : t('payment.errorHint', {
                  defaultValue: 'Une erreur est survenue lors du paiement. Veuillez réessayer.',
                })}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              data-testid="step4-retry-btn"
              onClick={onBookAnother}
              className="h-12 bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 rounded-lg px-6"
            >
              {t('payment.retry', { defaultValue: 'Réessayer' })}
            </Button>
            <Button
              variant="outline"
              onClick={onGoDashboard}
              className="h-12 border-[#1A3C34] text-[#1A3C34] hover:bg-[#F0EFEA] rounded-lg px-6"
            >
              {t('payment.dashboard', { defaultValue: 'Tableau de bord' })}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
